// Main entry point for landing page
import './styles/main.css';
import './styles/components.css';
import './styles/animations.css';
import { initSentry } from './shared/sentry-config.ts';
import * as Sentry from '@sentry/react';
// Initialize GA4 analytics
import { trackPageView } from './shared/services/analyticsService.ts';
import { initI18n } from './utils/i18n.js';
import { initSmoothScroll } from './utils/scroll.js';
import { initAnimations } from './utils/animations.js';
import { initHeroScrollParallax } from './utils/heroScrollParallax.js';
import { Header } from './components/landing/header.js';
import { ScreenshotCarousel } from './components/landing/screenshotCarousel.js';
import { FAQ } from './components/landing/faq.js';
import { initFooterCookieNotice } from './components/landing/footerCookieNotice.js';
import { open as openLightbox } from './components/landing/imageLightbox.js';

function getScreenshotUrl(lang, index) {
  return `/screenshots/${lang}/${lang}_${index}.webp`;
}

function applyImageFallback(img, { primarySrc, fallbackSrc }) {
  const placeholder = img.nextElementSibling;
  if (placeholder && placeholder.classList.contains('hero-screenshot-placeholder')) {
    placeholder.style.display = 'none';
  }

  let triedFallback = false;

  img.addEventListener('load', () => {
    if (placeholder && placeholder.classList.contains('hero-screenshot-placeholder')) {
      placeholder.style.display = 'none';
    }
    img.style.display = '';
  });

  img.addEventListener('error', () => {
    if (!triedFallback) {
      triedFallback = true;
      img.src = fallbackSrc;
      return;
    }

    img.style.display = 'none';
    if (placeholder && placeholder.classList.contains('hero-screenshot-placeholder')) {
      placeholder.style.display = 'flex';
    }
  });

  img.src = primarySrc;
}

// Initialize Sentry before other code
initSentry('web-landing', 'landing');

// Error handling with Sentry
window.addEventListener('error', (e) => {
  console.error('Global error:', e.error);
  Sentry.captureException(e.error, {
    tags: {
      errorType: 'global',
    },
  });
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled promise rejection:', e.reason);
  Sentry.captureException(e.reason, {
    tags: {
      errorType: 'unhandledRejection',
    },
  });
});

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  // Track page view
  trackPageView('Landing');
  
  // Initialize i18n
  await initI18n();

  // Localized screenshots (hero + carousel)
  const { getCurrentLanguage } = await import('./shared/utils/urlUtils.ts');
  const lang = getCurrentLanguage();

  const heroImg = document.getElementById('heroScreenshot');
  if (heroImg instanceof HTMLImageElement) {
    applyImageFallback(heroImg, {
      primarySrc: getScreenshotUrl(lang, 1),
      fallbackSrc: getScreenshotUrl('en', 1),
    });
    heroImg.addEventListener('click', () => {
      if (heroImg.src) openLightbox(heroImg.src, heroImg.alt);
    });
  }
  
  // Initialize footer cookie notice (after i18n is ready)
  initFooterCookieNotice();
  
  // Initialize smooth scroll
  initSmoothScroll(80);
  
  // Initialize animations
  initAnimations();

  initHeroScrollParallax();
  
  // Initialize header
  new Header();
  
  // Initialize screenshot carousel
  const screenshotContainer = document.getElementById('screenshotCarousel');
  if (screenshotContainer) {
    new ScreenshotCarousel(screenshotContainer, { lang, fallbackLang: 'en', firstIndex: 2, count: 5 });
  }
  
  // Initialize FAQ
  const faqContainer = document.getElementById('faqList');
  if (faqContainer) {
    new FAQ(faqContainer);
  }
  
  // Redirect to login page on login button click
  document.getElementById('loginBtn')?.addEventListener('click', async () => {
    const { redirectWithLanguage } = await import('./shared/utils/urlUtils.ts');
    redirectWithLanguage('/login');
  });
  
  // Redirect to login page on pricing "Get Started" buttons
  const handlePricingButtonClick = async (e) => {
    const { redirectWithLanguage } = await import('./shared/utils/urlUtils.ts');
    redirectWithLanguage('/login');
  };
  
  document.getElementById('pricingFreeBtn')?.addEventListener('click', handlePricingButtonClick);
  document.getElementById('pricingProBtn')?.addEventListener('click', handlePricingButtonClick);
  
  // Handle footer links navigation
  const privacyLink = document.getElementById('privacyLink');
  const termsLink = document.getElementById('termsLink');
  
  if (privacyLink) {
    privacyLink.addEventListener('click', async (e) => {
      e.preventDefault();
      const { redirectWithLanguage } = await import('./shared/utils/urlUtils.ts');
      redirectWithLanguage('/privacy');
    });
  }
  
  if (termsLink) {
    termsLink.addEventListener('click', async (e) => {
      e.preventDefault();
      const { redirectWithLanguage } = await import('./shared/utils/urlUtils.ts');
      redirectWithLanguage('/terms');
    });
  }
  
  // Handle support link navigation
  const supportLink = document.getElementById('supportLink');
  if (supportLink) {
    supportLink.addEventListener('click', async (e) => {
      e.preventDefault();
      const { redirectWithLanguage } = await import('./shared/utils/urlUtils.ts');
      redirectWithLanguage('/support');
    });
  }
  
  // Initialize sticky CTA bar for mobile
  initStickyCtaBar();
  
  // Initialize analytics tracking for CTAs
  initCtaTracking();
  
  // Initialize lazy loading for images
  initLazyLoading();
});

// Sticky CTA Bar for Mobile
function initStickyCtaBar() {
  const stickyCtaBar = document.getElementById('stickyCtaBar');
  if (!stickyCtaBar) return;
  
  const hero = document.getElementById('hero');
  if (!hero) return;
  
  const heroBottom = hero.offsetTop + hero.offsetHeight;
  
  function updateStickyCta() {
    if (window.innerWidth <= 767) {
      const scrollY = window.scrollY || window.pageYOffset;
      if (scrollY > heroBottom) {
        stickyCtaBar.classList.add('visible');
      } else {
        stickyCtaBar.classList.remove('visible');
      }
    } else {
      stickyCtaBar.classList.remove('visible');
    }
  }
  
  window.addEventListener('scroll', updateStickyCta, { passive: true });
  window.addEventListener('resize', updateStickyCta);
  updateStickyCta();
}

// Analytics tracking for CTAs
function initCtaTracking() {
  const ctaButtons = document.querySelectorAll('[data-analytics]');
  ctaButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      const analyticsId = button.getAttribute('data-analytics');
      if (analyticsId && typeof trackPageView === 'function') {
        // Track CTA click
        if (window.gtag) {
          window.gtag('event', 'cta_click', {
            'cta_id': analyticsId,
            'cta_text': button.textContent.trim(),
            'cta_location': button.closest('section')?.id || 'unknown'
          });
        }
      }
    });
  });
}

// Lazy loading for images
function initLazyLoading() {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.classList.add('loaded');
          }
          observer.unobserve(img);
        }
      });
    });
    
    // Observe all images with data-src attribute
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  } else {
    // Fallback for browsers without IntersectionObserver
    document.querySelectorAll('img[data-src]').forEach(img => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  }
}
